import { useRef, useCallback, useContext } from 'react'
import { useGetGotchiOwnerLazyQuery, getApolloClientSingleton } from 'vvs-gotchi-graphql'
import { useAppSelector } from 'state'
import useAuth from 'hooks/useAuth'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { AuthModalContext } from 'views/VvsGotchi/AuthModal/ContextProvider'

import { isAuthTokenValid } from '../auth'
import { useRefreshAuth } from './useSignAuthAndAcceptance'

/**
 * Get if authentication valid for VvsGotchi graphql endpoint
 */
export const useGotchiAuthValid = () => {
  const [accessToken] = useGotchiAuthState()
  return isAuthTokenValid(accessToken)
}

let runningEnsureAuth: Promise<void> = null
/**
 * VvsGotchi graphql endpoint authentication hook
 *
 * this returns a tuple of `[authValid, ensureAuth]`, where
 * `authValid` is true when accessToken exists for connected account and is valid (not expired)
 * `ensureAuth` can be used to ensure user is authenticated before API call that require accessToken:
 *
 * ```
 * const [authValid, ensureAuth] = useGotchiAuth()
 * const [someMutation] = useSomeMutation()
 * const someAction = useCallback(async () => {
 *   await ensureAuth()
 *   await someMutation( ... ) // require auth
 * }, [ ... ])
 * ```
 */
export const useGotchiAuth = () => {
  const authState = useGotchiAuthState()
  const authStateRef = useRef<typeof authState>()
  authStateRef.current = authState
  const { logout } = useAuth()
  const [getGotchiOwner] = useGetGotchiOwnerLazyQuery({
    client: getApolloClientSingleton(),
  })
  const refreshAuth = useRefreshAuth()
  const { startAuthModal } = useContext(AuthModalContext)

  const ensureAuth = useCallback(() => {
    if (!runningEnsureAuth) {
      runningEnsureAuth = (async () => {
        const [accessToken, account] = authStateRef.current

        if (isAuthTokenValid(accessToken)) {
          // test if accessToken is really valid on backend
          const { error } = await getGotchiOwner({ variables: { address: account } })
          if (!error) return
          console.error('Authentication is invalid on backend', error)
        }

        const refreshOk = await refreshAuth(accessToken, account)
        if (refreshOk) return

        const result = await startAuthModal()
        if (result === 'closed') {
          logout()
          throw new Error('Authentication modal closed')
        }
      })().finally(() => {
        runningEnsureAuth = null
      })
    }

    return runningEnsureAuth
  }, [logout, startAuthModal, refreshAuth, getGotchiOwner])

  return [isAuthTokenValid(authState[0]), ensureAuth] as const
}

const useGotchiAuthState = () => {
  const { account } = useActiveWeb3React()
  const accessToken = useAppSelector((state) => state.vvsgotchi.accessToken[account])
  return [accessToken, account] as const
}
